// Types and constants
import { HealthcareService } from "fhir/r5";
import {
  countHierarchyByLevel,
  countHierarchyLeaves,
  countHierarchyNodes,
  filterHierarchyTree,
  getHierarchyPath,
  HierarchyFilterResult,
  HierarchyNode,
  normalizeHierarchySearchValue,
  sortHierarchyNodes,
} from "./hierarchyTree";
import {
  getOfficialIdentifierValue,
  getReferenceValue,
  normalizeReferenceValue,
} from "./fhirReferences";

const HEALTHCARE_SERVICE_OFFERED_IN_EXTENSION_URL =
  "http://hl7.org/fhir/v5.0/StructureDefinition/extension-HealthcareService.offeredIn";

type HealthcareServiceReferenceLike = {
  identifier?: {
    value?: string | null;
  } | null;
  type?: string | null;
  reference?: string | null;
  display?: string | null;
};

export type HealthcareServiceNode = HierarchyNode<HealthcareService>;
export type HealthcareServiceFilterResult = HierarchyFilterResult<HealthcareService>;

// Normalization and matching
function normalizeHealthcareServiceMatchValue(value?: string | null): string {
  return normalizeHierarchySearchValue(normalizeReferenceValue(value));
}

function getReferenceCandidates(
  reference?: HealthcareServiceReferenceLike | null
): string[] {
  return [
    normalizeHealthcareServiceMatchValue(reference?.identifier?.value),
    normalizeHealthcareServiceMatchValue(reference?.reference?.split("/").pop()),
    normalizeHealthcareServiceMatchValue(reference?.display),
  ].filter(Boolean);
}

function isHealthcareServiceLinkExtensionUrl(url?: string | null): boolean {
  const normalizedUrl = (url ?? "").toLowerCase();

  return (
    normalizedUrl === HEALTHCARE_SERVICE_OFFERED_IN_EXTENSION_URL.toLowerCase() ||
    normalizedUrl.includes("offeredin") ||
    normalizedUrl.includes("crossversion") ||
    normalizedUrl.includes("cross-version")
  );
}

function getHealthcareServiceLinkReferences(
  healthcareService: HealthcareService
): HealthcareServiceReferenceLike[] {
  const extensionReferences = (
    healthcareService.extension
      ?.filter(
        (extension) =>
          Boolean(extension.valueReference) &&
          (isHealthcareServiceLinkExtensionUrl(extension.url) ||
            extension.valueReference?.type === "HealthcareService")
      )
      .map((extension) => extension.valueReference)
      .filter((reference) => Boolean(reference)) ?? []
  ) as HealthcareServiceReferenceLike[];

  const offeredInReferences = (healthcareService.offeredIn ?? []) as HealthcareServiceReferenceLike[];

  return [...offeredInReferences, ...extensionReferences].filter((reference) => {
    if (!reference) {
      return false;
    }

    return !reference.type || reference.type === "HealthcareService";
  });
}

function getHealthcareServiceReferenceValues(
  healthcareService: HealthcareService
): string[] {
  return getHealthcareServiceLinkReferences(healthcareService)
    .map((reference) => getReferenceValue(reference))
    .filter(Boolean);
}

// Lookup helpers
function getHealthcareServiceLookupValues(
  healthcareService: HealthcareService
): string[] {
  return [
    getHealthcareServiceKey(healthcareService),
    healthcareService.id,
    healthcareService.name,
    ...(healthcareService.identifier?.map((identifier) => identifier.value) ?? []),
  ]
    .filter(Boolean)
    .map((value) => normalizeHealthcareServiceMatchValue(value));
}

function resolveHealthcareServiceKeyByReference(
  healthcareServices: HealthcareService[],
  reference?: HealthcareServiceReferenceLike | null
): string {
  const candidates = getReferenceCandidates(reference);

  if (candidates.length === 0) {
    return "";
  }

  const match = healthcareServices.find((healthcareService) => {
    const healthcareServiceValues = getHealthcareServiceLookupValues(healthcareService);

    return candidates.some((candidate) =>
      healthcareServiceValues.some((healthcareServiceValue) => healthcareServiceValue === candidate)
    );
  });

  return match ? getHealthcareServiceKey(match) : "";
}

// Tree helpers
function getHealthcareServiceParentReferences(
  healthcareService: HealthcareService
): HealthcareServiceReferenceLike[] {
  return getHealthcareServiceLinkReferences(healthcareService);
}

// Public API
export function getHealthcareServiceKey(healthcareService: HealthcareService): string {
  return (
    getOfficialIdentifierValue(healthcareService.identifier) ||
    normalizeReferenceValue(healthcareService.id) ||
    normalizeReferenceValue(healthcareService.name)
  );
}

export function getHealthcareServiceDisplayName(
  healthcareService: HealthcareService
): string {
  return healthcareService.name?.trim() || healthcareService.id || "N/A";
}

export function getHealthcareServiceSearchTerms(
  healthcareService: HealthcareService
): string[] {
  return [
    healthcareService.id,
    healthcareService.name,
    ...(healthcareService.identifier?.map((identifier) => identifier.value) ?? []),
    ...getHealthcareServiceReferenceValues(healthcareService),
  ]
    .filter(Boolean)
    .map((value) =>
      normalizeHierarchySearchValue(normalizeReferenceValue(value))
    );
}

export function getHealthcareServiceParentKey(
  healthcareService: HealthcareService,
  healthcareServices: HealthcareService[]
): string {
  const references = getHealthcareServiceParentReferences(healthcareService);

  for (const reference of references) {
    const resolvedKey = resolveHealthcareServiceKeyByReference(
      healthcareServices,
      reference
    );

    if (resolvedKey) {
      return resolvedKey;
    }
  }

  return normalizeHealthcareServiceMatchValue(getReferenceValue(references[0]));
}

// Tree construction
export function buildHealthcareServiceTree(
  healthcareServices: HealthcareService[]
): HealthcareServiceNode[] {
  const nodeByKey = new Map<string, HealthcareServiceNode>();
  const parentByKey = new Map<string, string>();
  const childKeys = new Set<string>();

  healthcareServices.forEach((healthcareService) => {
    const key = getHealthcareServiceKey(healthcareService);
    if (!key) {
      return;
    }

    nodeByKey.set(key, { ...healthcareService, children: [] });

    const parentKey = getHealthcareServiceParentKey(healthcareService, healthcareServices);
    if (parentKey) {
      parentByKey.set(key, parentKey);
    }
  });

  parentByKey.forEach((parentKey, childKey) => {
    const parentNode = nodeByKey.get(parentKey);
    const childNode = nodeByKey.get(childKey);

    if (parentNode && childNode && parentKey !== childKey) {
      parentNode.children.push(childNode);
      childKeys.add(childKey);
    }
  });

  return sortHierarchyNodes(
    Array.from(nodeByKey.entries())
      .filter(([key]) => !childKeys.has(key))
      .map(([, node]) => node),
    getHealthcareServiceDisplayName
  );
}

// Metrics and filters
export function countHealthcareServiceNodes(
  nodes: HealthcareServiceNode[]
): number {
  return countHierarchyNodes(nodes);
}

export function countHealthcareServiceLeaves(
  nodes: HealthcareServiceNode[]
): number {
  return countHierarchyLeaves(nodes);
}

export function countHealthcareServicesByLevel(
  nodes: HealthcareServiceNode[]
): number[] {
  return countHierarchyByLevel(nodes);
}

export function normalizeHealthcareServiceSearchValue(value?: string | null): string {
  return normalizeHierarchySearchValue(value);
}

export function filterHealthcareServiceTree(
  nodes: HealthcareServiceNode[],
  searchTerm: string
): HealthcareServiceFilterResult {
  return filterHierarchyTree(
    nodes,
    normalizeHierarchySearchValue(searchTerm),
    getHealthcareServiceKey,
    getHealthcareServiceDisplayName,
    getHealthcareServiceSearchTerms
  );
}

export function getHealthcareServicePath(
  healthcareService: HealthcareService | null | undefined,
  healthcareServices: HealthcareService[]
): HealthcareService[] {
  return getHierarchyPath(
    healthcareService,
    healthcareServices,
    getHealthcareServiceKey,
    (item) => getHealthcareServiceParentKey(item, healthcareServices)
  );
}

export function getChildHealthcareServices(
  healthcareServices: HealthcareService[],
  parentHealthcareService: HealthcareService | null | undefined
): HealthcareService[] {
  if (!parentHealthcareService) {
    return [];
  }

  const parentKey = getHealthcareServiceKey(parentHealthcareService);

  if (!parentKey) {
    return [];
  }

  return healthcareServices.filter((candidate) => {
    if (getHealthcareServiceKey(candidate) === parentKey) {
      return false;
    }

    return (
      getHealthcareServiceParentKey(candidate, healthcareServices) === parentKey
    );
  });
}

export function getParentHealthcareServices(
  healthcareService: HealthcareService | null | undefined,
  healthcareServices: HealthcareService[]
): HealthcareService[] {
  if (!healthcareService) {
    return [];
  }

  const currentKey = getHealthcareServiceKey(healthcareService);
  const parentReferences = getHealthcareServiceParentReferences(healthcareService);
  const parentKeys = parentReferences
    .map((reference) =>
      resolveHealthcareServiceKeyByReference(healthcareServices, reference)
    )
    .filter(Boolean);

  const seen = new Set<string>();

  return parentKeys
    .map((parentKey) =>
      healthcareServices.find(
        (candidate) => getHealthcareServiceKey(candidate) === parentKey
      )
    )
    .filter((candidate): candidate is HealthcareService => {
      if (!candidate) {
        return false;
      }

      const key = getHealthcareServiceKey(candidate);
      if (!key || key === currentKey || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
}
